import React, { useState, useEffect, useMemo } from 'react';
import { toast } from 'sonner';
import { Trash2, User, Briefcase, AlertCircle } from 'lucide-react';
import { api, extractError } from '../lib/api';
import { fmtDate } from '../lib/date';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { ConfirmButton } from '../lib/confirm';

/**
 * Kompaktes Geräte-Einsatz-Badge (wird in Geräte-Plantafel UND normaler Plantafel genutzt).
 */
export function DeviceAssignmentChip({ assignment, device, project, driver, defaultDriver, onClick, compact = false, hideProject = false, warn = false }) {
  const hoursLabel = typeof assignment.hours === 'number'
    ? (Number.isInteger(assignment.hours) ? `${assignment.hours}h` : `${assignment.hours.toString().replace('.', ',')}h`)
    : '8h';
  const usedDriver = driver || defaultDriver;
  const missingDriver = !usedDriver;
  if (compact) {
    // Schlanke, einzeilige Darstellung für die normale Plantafel (Projektnummer entfällt, da im Cluster)
    return (
      <div
        className={`rounded px-1 py-[3px] text-[10px] leading-tight border-l-2 flex items-center gap-1 ${missingDriver ? 'bg-amber-50 border-amber-400' : 'bg-orange-50 border-orange-400'} ${onClick ? 'cursor-pointer hover:brightness-95' : ''}`}
        style={{ borderLeftColor: device?.color || '#f97316', boxShadow: warn ? '0 0 0 2px #be123c' : undefined }}
        onClick={onClick}
        data-testid={`device-chip-${assignment.id}`}
      >
        {warn && <AlertCircle size={11} className="text-rose-700 flex-shrink-0" />}
        {device?.name && <span className="font-bold text-orange-800 truncate">{device.name}</span>}
        <User size={9} className={`flex-shrink-0 ${missingDriver ? 'text-amber-700' : 'text-slate-600'}`}/>
        <span className={`truncate ${missingDriver ? 'text-amber-800 italic' : 'text-slate-700'}`}>
          {usedDriver ? `${usedDriver.first_name} ${usedDriver.last_name.slice(0, 1)}.` : 'ohne Fahrer!'}
        </span>
        <span className="ml-auto font-mono font-bold text-slate-600 text-[9px] flex-shrink-0">{hoursLabel}</span>
      </div>
    );
  }
  return (
    <div
      className={`rounded p-1 text-[10px] leading-tight border-l-2 ${missingDriver ? 'bg-amber-50 border-amber-400' : 'bg-orange-50 border-orange-400'} ${onClick ? 'cursor-pointer hover:brightness-95' : ''}`}
      style={{ borderLeftColor: device?.color || '#f97316', boxShadow: warn ? '0 0 0 2px #be123c' : undefined }}
      onClick={onClick}
      data-testid={`device-chip-${assignment.id}`}
    >
      {!hideProject && (project ? (
        <div className="flex items-center gap-1 font-semibold text-slate-900">
          <Briefcase size={9} className="flex-shrink-0"/>
          <span className="truncate">{project.project_number || project.name}</span>
        </div>
      ) : (
        <div className="text-slate-500 italic">— keine Baustelle —</div>
      ))}
      <div className="flex items-center gap-1 mt-0.5">
        {warn && <AlertCircle size={10} className="text-rose-700 flex-shrink-0" />}
        {device?.name && <span className="font-bold text-orange-800 truncate" style={{ maxWidth: 80 }}>{device.name}</span>}
        <User size={9} className={missingDriver ? 'text-amber-700' : 'text-slate-600'}/>
        <span className={`truncate ${missingDriver ? 'text-amber-800 italic' : 'text-slate-700'}`}>
          {usedDriver ? `${usedDriver.first_name} ${usedDriver.last_name.slice(0, 1)}.` : 'ohne Fahrer!'}
        </span>
        <span className="ml-auto font-mono font-bold text-slate-600 text-[9px]">{hoursLabel}</span>
      </div>
    </div>
  );
}

/**
 * Dialog zum Anlegen/Bearbeiten eines Geräte-Einsatzes.
 * state: { open, deviceId, date, assignment, defaultProjectId, defaultDriverId, defaultHours, defaultNotes }
 */
export function DeviceAssignDialog({ state, onClose, employees, projects, absences = [], devices = [], onSaved, canEdit = true, canDelete = true }) {
  const { open, deviceId, date, assignment } = state;
  const allowDeviceSelect = !deviceId && !assignment;
  const [projectId, setProjectId] = useState('__none__');
  const [driverId, setDriverId] = useState('__default__');
  const [deviceSel, setDeviceSel] = useState('');
  const [hours, setHours] = useState(8);
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) {
      setProjectId(state.defaultProjectId || '__none__');
      setDriverId(state.defaultDriverId ? state.defaultDriverId : '__default__');
      setDeviceSel(deviceId || '');
      setHours(state.defaultHours || 8);
      setNotes(state.defaultNotes || '');
    }
  }, [open, state.defaultDriverId, state.defaultHours, state.defaultProjectId, state.defaultNotes, deviceId]);

  const effectiveDeviceId = deviceId || deviceSel;

  const save = async () => {
    const h = Number(String(hours).replace(',', '.'));
    if (isNaN(h) || h <= 0 || h > 24) { toast.error('Ungültige Stundenzahl.'); return; }
    if (!effectiveDeviceId) { toast.error('Bitte ein Gerät auswählen.'); return; }
    // Notiz ist Pflicht. Bei einem Geräte-Einsatz steht hier, was der Fahrer
    // wissen muss – Werkzeug, Zufahrt, Ansprechpartner. Ohne Eintrag ruft er an.
    if (!notes.trim()) { toast.error('Bitte eine Notiz angeben (was ist zu beachten?).'); return; }
    setLoading(true);
    try {
      const payload = {
        device_id: effectiveDeviceId,
        date,
        hours: h,
        project_id: projectId === '__none__' ? null : projectId,
        driver_employee_id: driverId === '__default__' ? null : (driverId === '__none__' ? null : driverId),
        notes: notes.trim(),
      };
      if (assignment) {
        const { device_id, ...rest } = payload;
        await api.patch(`/device-assignments/${assignment.id}`, rest);
        toast.success('Geräte-Einsatz aktualisiert.');
      } else {
        await api.post('/device-assignments', payload);
        toast.success('Geräte-Einsatz angelegt.');
      }
      onSaved?.();
      onClose();
    } catch (err) { toast.error(extractError(err)); } finally { setLoading(false); }
  };

  const removeAssign = async () => {
    if (!assignment) return;
    setLoading(true);
    try {
      await api.delete(`/device-assignments/${assignment.id}`);
      toast.success('Geräte-Einsatz entfernt.');
      onSaved?.();
      onClose();
    } catch (err) { toast.error(extractError(err)); } finally { setLoading(false); }
  };

  const activeProjs = projects.filter(p => p.status === 'Aktiv');

  const eligibleDrivers = useMemo(() => {
    if (!open || !date) return [];
    const ersatzToday = new Set(
      absences.filter(ab => ab.type === 'Ersatz Geräte' && ab.date === date).map(ab => ab.employee_id)
    );
    return employees.filter(e =>
      e.active && (e.assignment_context === 'device' || ersatzToday.has(e.id))
    );
  }, [employees, absences, open, date]);

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{assignment ? 'Geräte-Einsatz bearbeiten' : 'Neuer Geräte-Einsatz'}</DialogTitle>
          <DialogDescription>Datum: <strong className="font-mono">{fmtDate(date)}</strong></DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          {allowDeviceSelect && (
            <div>
              <Label>Gerät</Label>
              <Select value={deviceSel} onValueChange={setDeviceSel}>
                <SelectTrigger data-testid="device-assign-device"><SelectValue placeholder="Gerät auswählen…"/></SelectTrigger>
                <SelectContent>
                  {devices.filter(d => d.active !== false && !d.deleted).map(d => (
                    <SelectItem key={d.id} value={d.id}>{d.name}{d.device_type ? ` (${d.device_type})` : ''}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          <div>
            <Label>Baustelle (optional)</Label>
            <Select value={projectId} onValueChange={setProjectId}>
              <SelectTrigger data-testid="device-assign-project"><SelectValue/></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">– keine Baustelle (z.B. Reparatur) –</SelectItem>
                {activeProjs.map(p => (
                  <SelectItem key={p.id} value={p.id}>
                    {p.project_number ? `#${p.project_number} ` : ''}{p.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Fahrer</Label>
            <Select value={driverId} onValueChange={setDriverId}>
              <SelectTrigger data-testid="device-assign-driver"><SelectValue/></SelectTrigger>
              <SelectContent>
                <SelectItem value="__default__">Standard-Fahrer verwenden</SelectItem>
                <SelectItem value="__none__">– kein Fahrer / offen –</SelectItem>
                {eligibleDrivers.length === 0 ? (
                  <div className="px-2 py-2 text-xs text-slate-400 italic">
                    Kein passender Fahrer verfügbar. Setze einen Mitarbeiter auf "Geräte" oder trage eine "Ersatz Geräte"-Abwesenheit für den {fmtDate(date)} ein.
                  </div>
                ) : eligibleDrivers.map(e => {
                  const isErsatz = e.assignment_context !== 'device';
                  return (
                    <SelectItem key={e.id} value={e.id}>
                      {e.first_name} {e.last_name}
                      {isErsatz ? ' (Ersatz)' : ' 🔧'}
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Stunden</Label>
            <div className="flex items-center gap-2">
              <Input type="number" step="0.05" min="0.25" max="24" value={hours} onChange={e => setHours(e.target.value)} className="w-24" data-testid="device-assign-hours"/>
              <div className="flex gap-1">
                {[2,4,6,8].map(h => (
                  <button key={h} type="button" onClick={() => setHours(h)} className={`text-[11px] font-semibold px-2 py-1 rounded border ${Number(hours) === h ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-700 border-slate-200 hover:border-slate-400'}`}>{h}h</button>
                ))}
              </div>
            </div>
          </div>
          <div>
            <Label>Notiz *</Label>
            <Textarea rows={2} value={notes} onChange={e => setNotes(e.target.value)} placeholder="z.B. Sonderauftrag, Werkzeug mitnehmen"/>
          </div>
        </div>
        <DialogFooter className="gap-2 sm:justify-between">
          {assignment && canDelete ? (
            <ConfirmButton
              onConfirm={removeAssign}
              disabled={loading}
              title="Geräte-Einsatz entfernen?"
              description="Der Eintrag wird endgültig aus der Plantafel gelöscht."
              confirmLabel="Löschen"
              variant="destructive"
              data-testid="device-assign-delete"
              className="text-rose-600 border-rose-200 hover:bg-rose-50"
              buttonVariant="outline"
            >
              <Trash2 size={14} className="mr-1"/>Löschen
            </ConfirmButton>
          ) : <div/>}
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose}>Abbrechen</Button>
            {canEdit && <Button onClick={save} disabled={loading} data-testid="device-assign-save">Speichern</Button>}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
